import { Injectable } from '@angular/core';
import {
  ResultStatus,
  TransactionStatus,
} from 'src/app/shared/types/transaction-status';
import {
  Question,
  QuestionType,
  QuestionOption,
  QuestionStatus,
  LinePick,
  Transaction,
  TransactionLine,
} from '@longnecktech/splash-commons-fe';

@Injectable({
  providedIn: 'root',
})
export class ResultsService {
  constructor() {}

  getResultsStatus(content?: Transaction): ResultStatus {
    if (!content) {
      return ResultStatus.PENDING;
    }

    const status = content.status;

    switch (true) {
      case this.isSubmitted(content):
        return ResultStatus.SUBMITTED;
      case this.isPending(content):
        return ResultStatus.PENDING;
      case status === TransactionStatus.WON:
        return ResultStatus.WON;
      case status === TransactionStatus.LOST:
        return ResultStatus.LOST;
      case status === TransactionStatus.VOIDED:
        return ResultStatus.VOIDED;
      default:
        return ResultStatus.PENDING;
    }
  }

  private isSubmitted(content?: Transaction): boolean {
    if (!content) {
      return false;
    }
    if (content.quizLines[0]?.correctPicks === null) {
      return true;
    }

    return content.quizGame.questions.every(
      (question) =>
        question.type === QuestionType.Options &&
        question.options.every(
          (option) => option.outcome === QuestionStatus.PENDING,
        ),
    );
  }

  private isPending(content?: Transaction): boolean {
    if (content?.quizLines[0]?.correctPicks === null) {
      return false;
    }
    return !this.isSubmitted() && content?.status === TransactionStatus.PENDING;
  }

  getTotalPrizes(quizLines?: TransactionLine[]): number {
    return (
      quizLines?.reduce((acc, currentLine) => {
        if (currentLine.prize) {
          acc += currentLine.prize.value;
        }
        return acc;
      }, 0) ?? 0
    );
  }

  getTotalStakeValue(content?: Transaction): number {
    if (!content?.quizLines || !content?.quizGame.stakeValue) {
      return 0;
    }
    return content?.quizLines.length * content?.quizGame.stakeValue;
  }

  processQuestion(
    question: Question,
    quizLines: TransactionLine[],
  ): QuestionOption[] {
    return quizLines.reduce((acc: QuestionOption[], line: TransactionLine) => {
      return this.processLine(line, question, acc);
    }, [] as QuestionOption[]);
  }

  private processLine(
    line: TransactionLine,
    question: Question,
    currentOptions: QuestionOption[],
  ): QuestionOption[] {
    return line.picks.reduce((acc, pick) => {
      if (pick.questionUuid === question.uuid) {
        return this.processPick(pick, question, acc);
      }
      return acc;
    }, currentOptions);
  }

  private processPick(
    pick: LinePick,
    question: Question,
    currentOptions: QuestionOption[],
  ): QuestionOption[] {
    const pickedOption = question.options.find(
      (option) => option.uuid === pick.pickedUuid,
    );

    if (
      pickedOption &&
      !currentOptions.some((option) => option.uuid === pickedOption.uuid)
    ) {
      return [...currentOptions, { ...pickedOption, isPicked: true }];
    }

    return currentOptions;
  }
}
